import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import {
    clearAlcoholConsumed,
    deleteAlcoholConsumed,
    loadAlcoholConsumed
} from '../../../../store/participantService';
import { scrollToFocus } from '../../../../utils/eventUtils';
import { DEFAULT_TABLE_ROWS_PER_PAGE } from '../../../../utils/formValidation/commonConstants';
import { ALCOHOL_CONSUMED_ERROR } from '../../../../utils/formValidation/loadingErrorMessageConstants';
import { stableSort } from '../../../../utils/sortFunctions';
import Button from '../../../formElements/Button';
import ResultsTable from '../../../table/ResultsTable';
import LoadingSpinner from '../../../ui/LoadingSpinner';

import AlcoholConsumedRow from './AlcoholConsumedRow';
import CreateAlcoholConsumed from './CreateAlcoholConsumed';

import app from '../../../../app.module.css';
import local from '../wellbeing.module.css';

const headCells = [
    { id: 'eventDate', numeric: false, label: 'Date' },
    {
        id: 'unitConsumed',
        numeric: true,
        label: 'Units consumed in last seven days'
    }
];

const initialRowMetaData = {
    order: 'asc',
    orderBy: 'eventDate',
    page: 0,
    rowsPerPage: DEFAULT_TABLE_ROWS_PER_PAGE
};

const AlcoholConsumed = ({
    participant,
    isDisabled = true,
    isDeleteDisabled = true,
    isAuditable,
    onSearchAuditItems
}) => {
    const dispatch = useDispatch();
    const alcoholConsumedRef = useRef(null);

    // LOCAL STATE
    const [rows, setRows] = useState([]);
    const [selectedRow, setSelectedRow] = useState({});
    const [showForm, setShowForm] = useState(false);
    const [formType, setFormType] = useState('');
    const [rowMetaData, setRowMetaData] = useState(initialRowMetaData);
    const [loadingContentMessage, setLoadingContentMessage] = useState('');

    // STORE STATE
    const alcoholConsumed = useSelector(
        (state) => state.entities.participantService.alcoholConsumed
    );
    const alcoholConsumedMetaData = useSelector(
        (state) => state.entities.participantService.alcoholConsumedMetaData
    );

    // USE EFFECTS
    useEffect(
        () => () => {
            cleanUp();
        },
        []
    );

    useEffect(() => {
        participant?.id && dispatch(loadAlcoholConsumed(participant.id));
    }, [participant?.id]);

    useEffect(() => {
        if (alcoholConsumedMetaData?.loaded) {
            if (!alcoholConsumed?.length) {
                rows.length > 0 && setRows([]);
                setLoadingContentMessage(ALCOHOL_CONSUMED_ERROR);
            } else setRows([...alcoholConsumed]);
        }
    }, [alcoholConsumed, alcoholConsumedMetaData]);

    // FNs
    const createRows = () =>
        stableSort(rows, rowMetaData.orderBy, rowMetaData.order).map((el) => (
            <AlcoholConsumedRow
                key={el.id}
                row={el}
                isDisabled={isDisabled || showForm}
                isDeleteDisabled={isDeleteDisabled || showForm}
                onEdit={onEdit}
                onDelete={onDelete}
                isAuditable={isAuditable}
                onSearchAuditItems={onSearchAuditItems}
            />
        ));

    const cleanUp = () => {
        dispatch(clearAlcoholConsumed());
    };

    // EVENT HANDLERS
    const onCreate = () => {
        setFormType('create');
        setSelectedRow({});
        setShowForm(true);
    };

    const onEdit = (e, row) => {
        e.stopPropagation();
        setSelectedRow(row);
        setFormType('edit');
        setShowForm(true);
    };

    const onDelete = (row) => {
        setSelectedRow(row);
        dispatch(deleteAlcoholConsumed(row.id));
    };

    const onClose = () => {
        setShowForm(false);
        setSelectedRow({});
        scrollToFocus(alcoholConsumedRef);
    };

    // RENDER
    return (
        <div
            className={local.wellBeingSection}
            data-testid="alcohol_consumed"
            ref={alcoholConsumedRef}>
            <h2 className={app.sectionHeading}>Alcohol Consumed</h2>
            <div className={local.tableSection}>
                {rows.length < 1 ? (
                    <LoadingSpinner content={loadingContentMessage} />
                ) : (
                    <ResultsTable
                        defaultOrderBy={rowMetaData.orderBy}
                        sortOrder={rowMetaData.order}
                        headCells={headCells}
                        passRowMetaDataUp={setRowMetaData}
                        tableRows={createRows()}
                        totalResults={rows.length}
                    />
                )}
            </div>
            <div>
                {!showForm ? (
                    <Button
                        id="addAlcoholConsumedButton"
                        type="button"
                        disabled={isDisabled}
                        content="Add New Alcohol Consumed"
                        icon={true}
                        onClick={onCreate}
                    />
                ) : (
                    <CreateAlcoholConsumed
                        participant={participant}
                        onClose={onClose}
                        isDisabled={isDisabled}
                        formType={formType}
                        row={selectedRow}
                    />
                )}
            </div>
        </div>
    );
};

AlcoholConsumed.propTypes = {
    participant: PropTypes.object.isRequired,
    isDisabled: PropTypes.bool,
    isDeleteDisabled: PropTypes.bool,
    isAuditable: PropTypes.bool,
    onSearchAuditItems: PropTypes.func
};

export default AlcoholConsumed;
